import React, { useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { PhotoAvatar } from "@/components/common/PhotoAvatar";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SourceSelect } from "@/components/common/SourceSelect";
import { Camera, Trash2, X, Save, Workflow, ShieldAlert, UserCog, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { AddressSections } from "./AddressSections";

const ADDR_KEYS = ["home_address", "home_address2", "home_city", "home_state", "home_postal_code", "home_country",
  "office_address", "office_address2", "office_city", "office_state", "office_zip", "office_country"];

function F({ label, children }) {
  return <div><Label className="text-xs font-semibold text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>;
}

export function PersonEditDrawer({ person, orgs, classTypes, candTypes, primaryOpp, onClose, onSaved, onChangeCandidateType, onPhotoChanged }) {
  const [form, setForm] = useState({ ...person, communication_preferences: person.communication_preferences || [] });
  const [cf, setCf] = useState(person.custom_fields || {});
  const [confirmOrg, setConfirmOrg] = useState(false);
  const [classes, setClasses] = useState(person.classifications || []);
  const [saving, setSaving] = useState(false);
  const [photoKey, setPhotoKey] = useState(0);
  const [hasPhoto, setHasPhoto] = useState(!!person.photo_path);
  const fileRef = useRef();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setCF = (k) => (e) => setCf({ ...cf, [k]: e.target.value });
  const orgName = (id) => orgs.find((o) => o.id === id)?.name || "Unassigned";

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/persons/${person.id}/photo`, fd); setHasPhoto(true); setPhotoKey((k) => k + 1); onPhotoChanged?.(true); toast.success("Photo updated"); }
    catch (err) { toast.error(apiError(err)); } finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const removePhoto = async () => {
    try { await api.delete(`/persons/${person.id}/photo`); setHasPhoto(false); setPhotoKey((k) => k + 1); onPhotoChanged?.(false); toast.success("Photo removed"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const toggleClass = async (key) => {
    try {
      if (classes.includes(key)) { await api.delete(`/persons/${person.id}/classifications/${key}`); setClasses(classes.filter((c) => c !== key)); }
      else { await api.post(`/persons/${person.id}/classifications`, { classification: key }); setClasses([...classes, key]); }
    } catch (e) { toast.error(apiError(e)); }
  };
  const doSave = async () => {
    setConfirmOrg(false); setSaving(true);
    try {
      await api.patch(`/persons/${person.id}`, {
        first_name: form.first_name, middle_name: form.middle_name, last_name: form.last_name,
        preferred_name: form.preferred_name, email: form.email || null, alt_email: form.alt_email,
        mobile: form.mobile, phone: form.phone, linkedin: form.linkedin, website: form.website,
        ...Object.fromEntries(ADDR_KEYS.map((k) => [k, form[k] || ""])),
        communication_preferences: form.communication_preferences,
        email_opt_out: !!form.email_opt_out, do_not_contact: !!form.do_not_contact,
        current_employer: form.current_employer, current_title: form.current_title,
        source: form.source || "", source_id: form.source_id || "", status: form.status || null,
        organization_id: form.organization_id || null,
        custom_fields: {
          agent_number: cf.agent_number || null, agency_contract_version: cf.agency_contract_version || null,
          appointment_date: cf.appointment_date || null,
          office_phone: cf.office_phone || null,
        },
      });
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const orgChanged = (form.organization_id || null) !== (person.organization_id || null);
  const save = () => { if (orgChanged) setConfirmOrg(true); else doSave(); };
  const COMM = ["Email", "Text", "Phone Call", "Teams"];
  const toggleComm = (v) => { const cur = form.communication_preferences || []; setForm({ ...form, communication_preferences: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] }); };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="bg-white w-full sm:max-w-xl overflow-y-auto tr-scroll" data-testid="person-edit-drawer">
        <SheetHeader><SheetTitle>Edit Person</SheetTitle></SheetHeader>

        <div className="flex items-center gap-4 py-4 border-b border-slate-100">
          <PhotoAvatar personId={person.id} name={`${form.first_name} ${form.last_name}`} size={64} hasPhoto={hasPhoto} refreshKey={photoKey} />
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPhoto} className="hidden" data-testid="photo-file-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="change-photo-btn"><Camera className="w-4 h-4 mr-1.5" /> {hasPhoto ? "Replace" : "Upload"} Photo</Button>
            {hasPhoto && <Button variant="outline" size="sm" onClick={removePhoto} data-testid="remove-photo-btn" className="text-rose-600"><Trash2 className="w-4 h-4" /></Button>}
          </div>
        </div>

        <div className="space-y-4 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Basic Information</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="First Name"><Input value={form.first_name || ""} onChange={set("first_name")} data-testid="edit-first-name" /></F>
            <F label="Middle Name"><Input value={form.middle_name || ""} onChange={set("middle_name")} /></F>
            <F label="Last Name"><Input value={form.last_name || ""} onChange={set("last_name")} data-testid="edit-last-name" /></F>
            <F label="Preferred / Goes By"><Input value={form.preferred_name || ""} onChange={set("preferred_name")} /></F>
            <F label="Primary Email"><Input value={form.email || ""} onChange={set("email")} data-testid="edit-email" /></F>
            <F label="Secondary Email"><Input value={form.alt_email || ""} onChange={set("alt_email")} /></F>
            <F label="Mobile"><Input value={form.mobile || ""} onChange={set("mobile")} data-testid="edit-mobile" /></F>
            <F label="Phone"><Input value={form.phone || ""} onChange={set("phone")} /></F>
            <F label="Office Phone"><Input value={cf.office_phone || ""} onChange={setCF("office_phone")} data-testid="edit-office-phone" /></F>
            <F label="LinkedIn"><Input value={form.linkedin || ""} onChange={set("linkedin")} /></F>
            <F label="Website"><Input value={form.website || ""} onChange={set("website")} /></F>
            <F label="Organization">
              <Select value={form.organization_id || ""} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                <SelectTrigger data-testid="edit-org"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="bg-white">{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>
          <F label="Communication Preferences">
            <div className="flex flex-wrap gap-1.5">
              {COMM.map((v) => (
                <button key={v} type="button" onClick={() => toggleComm(v)} data-testid={`comm-${v}`}
                  className={`text-xs rounded-full border px-2.5 py-1 ${(form.communication_preferences || []).includes(v) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-500"}`}>{v}</button>
              ))}
            </div>
          </F>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={!!form.email_opt_out} onCheckedChange={(v) => setForm({ ...form, email_opt_out: v })} data-testid="edit-email-opt-out" /> Email Opt Out</label>
            <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={!!form.do_not_contact} onCheckedChange={(v) => setForm({ ...form, do_not_contact: v })} data-testid="edit-do-not-contact" /> Do Not Contact</label>
          </div>

          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">Addresses</p>
          <AddressSections form={form} classifications={classes} onChange={(k, v) => setForm((s) => ({ ...s, [k]: v }))} />
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">Professional Details</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Job Title"><Input value={form.current_title || ""} onChange={set("current_title")} data-testid="edit-job-title" /></F>
            <F label="Company / Current Employer"><Input value={form.current_employer || ""} onChange={set("current_employer")} data-testid="edit-current-employer" /></F>
            <F label="Agent Code"><Input value={cf.agent_number || ""} onChange={setCF("agent_number")} data-testid="edit-agent-code" /></F>
            <F label="Agency Contract Version"><Input value={cf.agency_contract_version || ""} onChange={setCF("agency_contract_version")} data-testid="edit-agency-contract-version" /></F>
            <F label="Appt Date"><Input type="date" value={(cf.appointment_date || "").slice(0, 10)} onChange={setCF("appointment_date")} data-testid="edit-appt-date" /></F>
            <F label="Source"><SourceSelect organizationId={form.organization_id} value={form.source_id} label={form.source} testId="edit-source"
              onChange={({ source_id, source }) => setForm({ ...form, source_id, source })} /></F>
            <F label="Status">
              <Select value={form.status || "active"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {["active", "inactive", "prospect", "archived"].map((s) => (
                    <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>))}
                </SelectContent>
              </Select>
            </F>
          </div>
          <p className="text-[11px] text-slate-400 -mt-1">Agent Code is not globally unique — Agent Staff may share the parent agency's code. It is never used alone to merge or de-duplicate People.</p>

          {(person.assigned_recruiter_name || person.linked_user) && (
            <div className="grid sm:grid-cols-2 gap-3">
              {person.assigned_recruiter_name && (
                <div className="rounded-md border border-slate-200 p-3" data-testid="drawer-assigned-recruiter">
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><ArrowRightLeft className="w-3 h-3" /> Assigned Recruiter</p>
                  <p className="text-sm font-semibold text-slate-800 mt-0.5">{person.assigned_recruiter_name}</p>
                  <p className="text-[11px] text-slate-400 mt-1">Managed via the active Recruiting Opportunity.</p>
                </div>
              )}
              {person.linked_user && (
                <div className="rounded-md border border-slate-200 p-3" data-testid="drawer-linked-user">
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><UserCog className="w-3 h-3" /> Linked User (login)</p>
                  <p className="text-sm font-semibold text-slate-800 mt-0.5">{person.linked_user.name || person.linked_user.email}</p>
                  <Link to="/admin/users" className="text-[11px] text-brand font-medium hover:underline" data-testid="drawer-linked-user-link">Manage in User Administration →</Link>
                </div>
              )}
            </div>
          )}

          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">Classifications</p>
          <div className="flex flex-wrap gap-2" data-testid="edit-classifications">
            {classTypes.map((c) => (
              <button key={c.key} type="button" onClick={() => toggleClass(c.key)} data-testid={`edit-class-${c.key}`} disabled={c.assignable === false}
                title={c.assignable === false ? "Changed through its own workflow or by an administrator" : undefined}
                className={`text-xs rounded-full border px-2.5 py-1 disabled:opacity-50 disabled:cursor-not-allowed ${classes.includes(c.key) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-500"}`}>{c.label}</button>
            ))}
          </div>

          {primaryOpp && (
            <>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">Recruiting Opportunity</p>
              <div className="rounded-md border border-slate-200 p-3 flex items-center justify-between">
                <div className="text-sm text-slate-600">Candidate Type: <b>{candTypes.find((c) => c.key === primaryOpp.candidate_type)?.label || "—"}</b>
                  <span className="text-xs text-slate-400"> · {primaryOpp.pipeline_name || primaryOpp.pipeline?.name}</span></div>
                <Button variant="outline" size="sm" onClick={onChangeCandidateType} data-testid="drawer-change-candidate-type"><Workflow className="w-4 h-4 mr-1.5" /> Change Candidate Type</Button>
              </div>
              <p className="text-[11px] text-slate-400">Candidate Type belongs to the Opportunity — changing it preserves full pipeline/stage history.</p>
            </>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={onClose}><X className="w-4 h-4 mr-1" /> Close</Button>
          <Button onClick={save} disabled={saving} data-testid="drawer-save" className="bg-brand hover:bg-brand-strong"><Save className="w-4 h-4 mr-1" /> Save</Button>
        </SheetFooter>

        <Dialog open={confirmOrg} onOpenChange={(o) => !o && setConfirmOrg(false)}>
          <DialogContent className="bg-white max-w-md" data-testid="org-change-confirm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-500" /> Move this Person?</DialogTitle>
              <DialogDescription>
                Moving this Person from <b>{orgName(person.organization_id)}</b> to <b>{orgName(form.organization_id)}</b> may
                change which District users can view this record. The Person ID and all history are preserved.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOrg(false)} data-testid="org-change-cancel">Cancel</Button>
              <Button onClick={doSave} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="org-change-confirm-btn"><ArrowRightLeft className="w-4 h-4 mr-1" /> Move Person</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
